import { useEffect, useMemo, useState } from 'react';
import { useStepContext } from '../contexts/stepContext';
import { useSubscriptionContext } from '../contexts/subscriptionContext';
import { useConfigContext } from '../contexts/configContext';
import { useAppSelector, useLocaleDateFormat, useTrack } from '../hooks';
import { dayjs, requestPauseSubscription } from '../smi-bridge';
import { LocaleText, getLocaleMatch } from '../LocaleText';
import { Footer } from '../components/Footer';
import type { PauseNode } from '../types';

/**
 * PauseStep - Allows users to pause their subscription by delaying the next order date.
 *
 * ## Configuration
 *
 * Configure pause durations via `customPauseDurations` in step parameters:
 *
 * ```typescript
 * {
 *   type: 'cancel-flow:pause',
 *   name: 'pause',
 *   parameters: {
 *     customPauseDurations: [
 *       { value: 1, unit: 'week' },
 *       { value: 2, unit: 'frequency' },  // 2x the subscription frequency
 *       { value: 1, unit: 'month' }
 *     ],
 *   }
 * }
 * ```
 *
 * - `value`: Number of units to delay (positive integer)
 * - `unit`: One of 'day', 'week', 'month', or 'frequency'
 * - `frequency` entries are resolved against the subscription's frequency and period
 *   (e.g. value=2 on a monthly subscription → 2 months)
 * - Fixed and frequency units can be mixed in the same config
 * - Durations are automatically sorted from soonest to farthest
 * - Invalid entries (non-positive values, invalid units) are filtered out
 *
 * If `customPauseDurations` is not set or is empty, the step falls back to
 * the default [1, 2, 3] × subscription frequency.
 *
 */

const fixedUnits = ['day', 'week', 'month'] as const;
const allUnits = ['day', 'week', 'month', 'frequency'] as const;
type FixedUnit = (typeof fixedUnits)[number];
type PauseUnit = (typeof allUnits)[number];

export type PauseDuration = {
  value: number;
  unit: PauseUnit;
};

type PauseOption = {
  label: string;
  formattedDate: string;
  value: string;
  /** Duration in days, used for sorting */
  durationInDays: number;
};

export const PauseStep = ({ step }: { step: PauseNode }) => {
  const { dispatch } = useStepContext();
  const track = useTrack();
  const subscription = useSubscriptionContext();
  const config = useConfigContext();
  const orderItem = useAppSelector(state => state.order_items.find(oi => oi.subscription === subscription.public_id));
  const order = useAppSelector(state => state.orders.find(o => o.public_id === orderItem?.order));
  const locale = useAppSelector(state => state.locale);

  const [selectedOption, setSelectedOption] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const formatDate = useLocaleDateFormat();

  // Get custom pause durations from step config (if configured)
  const customPauseDurations = step.parameters.customPauseDurations;

  useEffect(() => {
    track('subscriptioncancel_pause_shown', { subscriptionId: subscription.public_id });
  }, [subscription.public_id, track]);

  // Calculate pause options - uses custom durations if configured, otherwise falls back to frequency multipliers
  const pauseOptions: PauseOption[] = useMemo(() => {
    if (!order || !subscription) return [];

    const baseDate = dayjs(order.place);
    const matchedLocale = getLocaleMatch(locale, Object.keys(config.locales || {}));
    const localeStrings = config.locales?.[matchedLocale] || {};

    // Check for valid custom pause durations
    const isValidDuration = (d: PauseDuration) =>
      d && Number.isInteger(d.value) && d.value > 0 && allUnits.includes(d.unit);

    const validCustomDurations = (customPauseDurations || []).filter(d => {
      if (!isValidDuration(d)) {
        console.warn('[cancel-flow] Invalid pause duration filtered out:', d);
        return false;
      }
      return true;
    });

    // Use custom durations if configured, otherwise default [1, 2, 3] × frequency
    const configuredDurations: PauseDuration[] =
      validCustomDurations.length > 0
        ? validCustomDurations
        : [1, 2, 3].map(multiplier => ({
            value: multiplier,
            unit: 'frequency' as const
          }));

    // Resolve 'frequency' entries to actual durations using subscription frequency
    const subUnit = fixedUnits[subscription.every_period - 1];
    const resolvedDurations = configuredDurations.map(d =>
      d.unit === 'frequency'
        ? { value: subscription.every * d.value, unit: subUnit }
        : { value: d.value, unit: d.unit as FixedUnit }
    );

    // Dedupe resolved durations — e.g. "2 weeks" fixed and "2x frequency" on a weekly subscription
    // would both resolve to { value: 2, unit: 'week' }
    const seen = new Set<string>();
    const uniqueDurations = resolvedDurations.filter(d => {
      const key = `${d.value}-${d.unit}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });

    const options: PauseOption[] = uniqueDurations.map(duration => {
      const pauseDate = baseDate.add(duration.value, duration.unit);
      return {
        label: formatFrequencyLabel(duration.value, duration.unit, localeStrings),
        formattedDate: formatDate(pauseDate),
        value: pauseDate.format('YYYY-MM-DD'),
        durationInDays: calculateDurationInDays(duration.value, duration.unit)
      };
    });

    // Sort by duration (soonest to farthest)
    return options.sort((a, b) => a.durationInDays - b.durationInDays);
  }, [order, subscription, locale, config, formatDate, customPauseDurations]);

  async function handlePause() {
    if (!selectedOption || !order) return;

    try {
      setIsSubmitting(true);
      await requestPauseSubscription({
        subscription_id: subscription.public_id,
        order_id: order.public_id,
        date: selectedOption
      });
      track('subscriptioncancel_completed_saved_paused', { subscriptionId: subscription.public_id });
      dispatch({ type: 'accepted' });
    } catch (error) {
      console.error('Error pausing subscription:', error);
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <div>
      <div className="og-dialog-body">
        <p>
          <LocaleText t="description" />
        </p>
        <div className="og-pause-subscription">
          {pauseOptions.map(option => (
            <div key={option.value} className="og-pause-subscription-option">
              <label>
                <input
                  type="radio"
                  name="pause-option"
                  value={option.value}
                  checked={selectedOption === option.value}
                  onChange={() => setSelectedOption(option.value)}
                />
                {option.label} <span className="og-pause-date">({option.formattedDate})</span>
              </label>
            </div>
          ))}
        </div>
      </div>

      <Footer>
        <button
          disabled={isSubmitting || !selectedOption}
          onClick={handlePause}
          className="og-button"
          data-variant="primary"
        >
          <LocaleText t="pauseButton" />
        </button>
      </Footer>
    </div>
  );
};

/**
 * Formats the frequency label for a pause option
 * e.g., "2 weeks", "4 weeks", "2 months"
 */
function formatFrequencyLabel(
  every: number,
  unit: 'day' | 'week' | 'month',
  localeStrings: Record<string, string>
): string {
  const pluralKey = `${unit}s`;
  const unitLabel = every === 1 ? localeStrings[unit] || unit : localeStrings[pluralKey] || pluralKey;

  return `${every} ${unitLabel}`;
}

/**
 * Converts a duration to approximate days for sorting purposes.
 * Uses average values: 1 week = 7 days, 1 month = 30 days
 */
function calculateDurationInDays(value: number, unit: 'day' | 'week' | 'month'): number {
  switch (unit) {
    case 'day':
      return value;
    case 'week':
      return value * 7;
    case 'month':
      return value * 30;
  }
}
